import { useCallback, useEffect, useRef, useState } from 'react';

import { toast } from '@/components/ui/toast';
import {
    NO_RESTING_ORDER_CODE,
    type LimitOpenRequest,
    type SpeedBackend,
    type TradeOpenRequest,
} from '@/lib/api/speed';
import {
    BusinessError,
    describeErrorText,
    getErrorMessage,
    isApiError,
} from '@/lib/errors';
import { uuid } from '@/lib/uuid';

import type {
    RecoveredTradePayload,
    TradeExecutionPhase,
    TradeExecutionStatus,
    TradeNotificationExecutionPhase,
    TradeStatus,
} from '../types';

const TOAST_DURATION = 10_000;
const ISOLATED_PHASE_FALLBACK_MS = 1_500;

/**
 * Map a backend `code` / message into a concise, user-facing error. The new
 * speed-trading API returns `{ error, code }` (HTTP 4xx/5xx) or a thrown
 * BusinessError, so most of the on-chain wallet error classification is gone.
 */
function classifyError(error: unknown): string {
    if (error instanceof BusinessError) {
        return error.getUserMessage();
    }

    const message = getErrorMessage(error);
    const lower = message.toLowerCase();

    if (lower.includes('insufficientbalance')) {
        return 'Insufficient balance. Deposit USDC before trading.';
    }
    if (
        lower.includes('activetradeexists') ||
        lower.includes('activepositionexists')
    ) {
        return 'You already have an open position for this market.';
    }
    if (lower.includes('activewithdrawexists')) {
        return 'A withdrawal is in progress. Wait for it to finish before trading.';
    }
    if (lower.includes('belowminorder')) {
        return 'Order is below the $10 minimum. Increase collateral or leverage.';
    }
    if (lower.includes('triggerunreachable')) {
        return 'The take profit or stop loss is not reachable at this leverage (the price would have to fall to $0). Lower the target or raise the leverage.';
    }
    if (
        lower.includes('leveragetierexceeded') ||
        lower.includes('invalidleverage')
    ) {
        return 'Leverage or size is outside the allowed range for this market.';
    }
    if (lower.includes('custodialwalletmissing')) {
        return 'No custodial wallet yet. Deposit USDC to provision one.';
    }
    if (lower.includes('ratelimited')) {
        return 'Too many requests. Please slow down and retry.';
    }
    if (lower.includes('noposition')) {
        return 'No live position to close.';
    }
    if (lower.includes('norestingorder')) {
        return 'This market has no resting limit order any more.';
    }
    if (lower.includes('orderunconfirmed')) {
        return 'This order is not confirmed on Hyperliquid yet, so it cannot be cancelled. Try again in a minute.';
    }
    if (lower.includes('badlimitprice')) {
        return 'Enter a limit price above zero.';
    }
    // HIP-3 builder-market fail-fast codes. The activation case is normally
    // intercepted upstream (useRealTradeActions seeds the activation banner);
    // this copy is the fallback for any other caller.
    if (lower.includes('hip3_activation_required')) {
        return 'This market needs a one-time activation before your first trade.';
    }
    if (lower.includes('hip3_disabled')) {
        return 'This market is temporarily disabled. Try another market.';
    }
    if (lower.includes('stale_market_data')) {
        return 'Market data is temporarily stale. Try again shortly.';
    }
    if (lower.includes('hip3_collateral_funding_required')) {
        return 'Your deposit is still settling into this market. Retry shortly.';
    }

    // nothing matched: the presenter decides what the user reads, so a zod
    // dump, a revert string or a stack can never reach the toast.
    return describeErrorText(error, { operation: 'Trade' });
}

function logStep(step: string, data?: unknown) {
    console.log(`[TradeExecution] ${step}`, data ?? '');
}

function logError(step: string, error: unknown) {
    console.error(`[TradeExecution] ${step} failed:`, error);
}

interface TradeExecutionState {
    status: TradeExecutionStatus;
    /** Hyperliquid coin of the active position, e.g. "BTC". */
    coin: string | null;
    /** Discovered via reconciliation / WS; the open response does not carry it. */
    tradeHash: string | null;
    openTxHash: string | null;
    closeTxHash: string | null;
    error: string | null;
    positionType: 'LONG' | 'SHORT' | null;
    positionAmount: number | null;
    entryPrice: number | null;
    /** Position leverage; PnL = priceMove% * leverage. */
    leverage: number | null;
    /**
     * The price a GTC limit entry rests (or rested) at, as the backend rounded
     * it. Set on every limit-origin slot and KEPT after the fill: it is the
     * "this started as a limit order" marker the UI reads, exactly like the
     * trade row's `limitPrice`. Null on a market open.
     */
    limitPrice: string | null;
    /**
     * Which limit-order request this client currently has in flight for this
     * coin, if any.
     *
     * It exists because "fill now" is TWO backend operations behind one button:
     * the resting order is cancelled (which publishes `Cancelled` for the old
     * hash) and only then is the market open placed, seconds later. Without
     * knowing a fill is running, that `Cancelled` push looks exactly like the
     * user's own cancel and resets the slot mid-request — the rail flips back to
     * the order form and the position that follows lands in an empty slot.
     */
    pendingAction: 'cancel' | 'fill' | null;
    /** UI-only detail while an isolated-only open request is in flight. */
    executionPhase: TradeExecutionPhase | null;
    /** Correlates the in-flight HTTP open with its websocket progress push. */
    clientRequestId: string | null;
    /**
     * When the position was opened (ms epoch): the accept time for a live open,
     * the backend `createdAt` for a restored one. Authoritative for every
     * "opened at" / "held for" readout — do NOT re-stamp it at render time.
     */
    openedAt: number | null;
    /** Measured request→fill latency of a live open; null when restored. */
    fillMs: number | null;
    /**
     * How the position entered this client. `live` = this session executed the
     * open (a real fill worth announcing); `restored` = hydrated from the API /
     * snapshot on reload or reconciliation, which must stay silent.
     */
    openSource: 'live' | 'restored' | null;
}

/**
 * Optional caller hook run in the catch path before the default error toast.
 * Return `true` to signal the error was fully handled (e.g. mapped to a
 * dedicated UI + toast) so the generic classified toast is suppressed.
 */
type TradeErrorHandler = (error: unknown) => boolean | void;

interface ExecuteOpenParams {
    backend: SpeedBackend;
    request: TradeOpenRequest;
    positionType: 'LONG' | 'SHORT';
    /** Collateral (USD) shown in the optimistic position visual. */
    positionAmount: number;
    snapshotEntryPrice: number;
    isolatedOnly?: boolean;
    onSuccess?: () => void;
    onError?: TradeErrorHandler;
}

interface ExecuteLimitOpenParams {
    backend: SpeedBackend;
    request: LimitOpenRequest;
    positionType: 'LONG' | 'SHORT';
    /** Collateral (USD) the order reserves. */
    positionAmount: number;
    onSuccess?: (result: { status: 'Resting' | 'Open' }) => void;
    onError?: TradeErrorHandler;
}

/** Shared by the two things you can do to a resting order: drop it, or take it
 *  at market. Both are by coin — one resting order per pair. */
interface ExecuteLimitActionParams {
    backend: SpeedBackend;
    coin: string;
    onSuccess?: (result: {
        /** what the order turned into: gone, or a live position. */
        status: 'Cancelled' | 'Open';
        tradeHash: string | null;
    }) => void;
    onError?: TradeErrorHandler;
}

interface ExecuteCloseParams {
    backend: SpeedBackend;
    coin: string;
    /** Receives the measured request→close latency so the POSITION CLOSED card
     *  can carry it (dev hosts only — see SpeedResultFx / isLatencyDebugHost). */
    onSuccess?: (result: { closeMs: number }) => void;
    onError?: TradeErrorHandler;
}

const INITIAL_STATE: TradeExecutionState = {
    status: 'idle',
    coin: null,
    tradeHash: null,
    openTxHash: null,
    closeTxHash: null,
    error: null,
    positionType: null,
    positionAmount: null,
    entryPrice: null,
    leverage: null,
    limitPrice: null,
    pendingAction: null,
    executionPhase: null,
    clientRequestId: null,
    openedAt: null,
    fillMs: null,
    openSource: null,
};

/** normalized map key — coin case is inconsistent across the stack. */
function key(coin: string | null | undefined): string | null {
    const trimmed = coin?.trim();
    return trimmed ? trimmed.toUpperCase() : null;
}

/**
 * Drives backend-executed (gasless) positions — ONE PER COIN. The Hyperliquid
 * backend does all on-chain work, so opening and closing are each a single
 * authenticated POST that returns a tx hash; the live `Open`/`Closed`
 * transitions arrive via the trade-status WebSocket (`applyTradeUpdate`) or REST
 * reconciliation (`hydrateFromRemoteTrade`).
 *
 * This used to hold a single `TradeExecutionState`, which is what actually
 * stopped a user opening BTC and ETH at the same time: the backend's uniqueness
 * constraint has always been per-coin, but `executeOpen` refused while the one
 * slot was anything other than idle. Slots are now keyed by coin.
 *
 * `executionLockRef` is deliberately NOT per-coin. MoonHub.API takes a
 * per-WALLET mutex for the whole of an open (`acquireWalletActiveSlot`, TTL 5
 * min), so a second concurrent open fails server-side with `activeWorkflowExists`
 * whatever the client does. Serialising here means the user gets a queue rather
 * than an error — sequential opens on different coins are unaffected, which is
 * the case this feature is about.
 */
export function useTradeExecution() {
    const [slots, setSlots] = useState<Record<string, TradeExecutionState>>({});
    const executionLockRef = useRef(false);
    const phaseFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeOpenRef = useRef<{
        clientRequestId: string;
        coin: string;
    } | null>(null);

    /**
     * Update one coin's slot. An idle result is DELETED rather than stored, so
     * `slots` stays the set of markets this client is actually driving — the
     * store mirrors that set, and a lingering idle entry would keep a closed
     * market on screen.
     */
    const patchSlot = useCallback(
        (
            coin: string | null | undefined,
            updater: (prev: TradeExecutionState) => TradeExecutionState,
        ) => {
            const k = key(coin);
            if (k === null) return;
            setSlots((prev) => {
                const cur = prev[k] ?? { ...INITIAL_STATE, coin: coin ?? null };
                const next = updater(cur);
                if (next === cur) return prev;
                if (next.status === 'idle' && next.tradeHash === null) {
                    if (!(k in prev)) return prev;
                    // keep the coin so the store's _syncExecution sees an idle
                    // slot for it once and clears it, instead of the entry simply
                    // vanishing with nothing to tell the store about.
                    return {
                        ...prev,
                        [k]: { ...INITIAL_STATE, coin: next.coin },
                    };
                }
                return { ...prev, [k]: next };
            });
        },
        [],
    );

    /** the coin whose slot carries `tradeHash`, or null. */
    const coinForTradeHash = useCallback(
        (tradeHash: string): string | null => {
            const hit = Object.values(slots).find(
                (s) => s.tradeHash === tradeHash,
            );
            return hit?.coin ?? null;
        },
        [slots],
    );

    const clearPhaseFallback = useCallback(() => {
        if (phaseFallbackRef.current !== null) {
            clearTimeout(phaseFallbackRef.current);
            phaseFallbackRef.current = null;
        }
    }, []);

    const clearExecutionPhase = useCallback(() => {
        const coin = activeOpenRef.current?.coin ?? null;
        clearPhaseFallback();
        activeOpenRef.current = null;
        if (coin === null) return;
        patchSlot(coin, (prev) => ({
            ...prev,
            executionPhase: null,
            clientRequestId: null,
        }));
    }, [clearPhaseFallback, patchSlot]);

    useEffect(
        () => () => {
            clearPhaseFallback();
            activeOpenRef.current = null;
        },
        [clearPhaseFallback],
    );

    /** Drop EVERY slot — mode switch / logout, not a per-trade transition. */
    const reset = useCallback(() => {
        clearPhaseFallback();
        activeOpenRef.current = null;
        setSlots((prev) =>
            Object.keys(prev).length === 0
                ? prev
                : Object.fromEntries(
                      Object.entries(prev).map(([k, s]) => [
                          k,
                          { ...INITIAL_STATE, coin: s.coin },
                      ]),
                  ),
        );
    }, [clearPhaseFallback]);

    /**
     * Hydrate from a recovered trade (GET /speed/trades or persisted snapshot):
     * treat it as a live open position so the close path + UI light up.
     */
    const hydrateFromRemoteTrade = useCallback(
        (payload: RecoveredTradePayload) => {
            clearPhaseFallback();
            activeOpenRef.current = null;
            // The backend's createdAt is the real open time; fall back to "now"
            // only if it is unparseable, so the UI never shows a NaN clock.
            const createdAtMs = Date.parse(payload.createdAt);
            patchSlot(payload.coin, () => ({
                status: 'open',
                coin: payload.coin,
                tradeHash: payload.tradeHash,
                openTxHash: payload.openTxHash,
                closeTxHash: null,
                error: null,
                positionType: payload.side,
                positionAmount: payload.amount,
                entryPrice: payload.entry,
                leverage: payload.leverage ?? null,
                limitPrice: payload.limitPrice ?? null,
                pendingAction: null,
                executionPhase: null,
                clientRequestId: null,
                openedAt: Number.isNaN(createdAtMs) ? Date.now() : createdAtMs,
                fillMs: null,
                openSource: 'restored',
            }));
        },
        [clearPhaseFallback, patchSlot],
    );

    /** Clear one market's slot, or every one when no coin is named (the
     *  cross-tab "snapshot removed" path, which cannot say which trade went). */
    const clearHydratedTrade = useCallback(
        (coin?: string | null) => {
            clearPhaseFallback();
            activeOpenRef.current = null;
            if (coin === undefined || coin === null) {
                reset();
                return;
            }
            patchSlot(coin, (prev) => ({ ...INITIAL_STATE, coin: prev.coin }));
        },
        [clearPhaseFallback, patchSlot, reset],
    );

    const executeOpen = useCallback(
        async ({
            backend,
            request,
            positionType,
            positionAmount,
            snapshotEntryPrice,
            isolatedOnly = false,
            onSuccess,
            onError,
        }: ExecuteOpenParams) => {
            if (executionLockRef.current) {
                logStep('Open skipped: another execution is in-flight');
                return;
            }
            // Per-coin, not global. A live BTC position must not stop an ETH
            // open — that is the whole point of the multi-position work. What
            // IS still refused is a second position on the SAME coin, which is
            // the rule the backend's unique index enforces and this mirrors so
            // the user gets a no-op rather than an `activeTradeExists` error.
            const openKey = key(request.coin);
            const existing = openKey === null ? undefined : slots[openKey];
            if (
                existing &&
                existing.status !== 'idle' &&
                existing.status !== 'failed'
            ) {
                logStep('Open skipped: this market already has a position', {
                    coin: request.coin,
                });
                return;
            }

            executionLockRef.current = true;
            const clientRequestId = uuid();
            const correlatedRequest = { ...request, clientRequestId };
            activeOpenRef.current = { clientRequestId, coin: request.coin };
            try {
                logStep('Open: submitting', correlatedRequest);
                patchSlot(request.coin, () => ({
                    ...INITIAL_STATE,
                    status: 'submitting',
                    coin: request.coin,
                    positionType,
                    positionAmount,
                    entryPrice: snapshotEntryPrice,
                    leverage: request.leverage,
                    executionPhase: isolatedOnly ? 'configuring' : null,
                    clientRequestId,
                }));

                if (isolatedOnly) {
                    phaseFallbackRef.current = setTimeout(() => {
                        phaseFallbackRef.current = null;
                        if (
                            activeOpenRef.current?.clientRequestId ===
                            clientRequestId
                        ) {
                            patchSlot(request.coin, (prev) => ({
                                ...prev,
                                executionPhase: 'submitting',
                            }));
                        }
                    }, ISOLATED_PHASE_FALLBACK_MS);
                }

                const submitTime = performance.now();
                const result = await backend.openTrade(correlatedRequest);
                const fillMs = Math.round(performance.now() - submitTime);
                logStep('Open: accepted', {
                    txHash: result.txHash,
                    coin: result.coin,
                    fillMs,
                });

                // the agent open is synchronous + authoritative: a non-error
                // response means the entry filled, so confirm 'open' now instead of
                // parking in 'pending' until a WS Open push that agent mode may
                // never send. a later WS Open event / REST reconciliation stays an
                // idempotent refinement (it only tightens the entry price).
                // the backend keys the trade by the order-submit txHash, so it is
                // both our openTxHash and the tradeHash the WS / history match on.
                patchSlot(request.coin, (prev) => ({
                    ...prev,
                    status: 'open',
                    tradeHash: result.txHash,
                    openTxHash: result.txHash,
                    executionPhase: null,
                    clientRequestId: null,
                    openedAt: Date.now(),
                    fillMs,
                    openSource: 'live',
                }));

                // No sonner "filled" toast: the chart-anchored POSITION OPENED
                // card (SpeedResultFx) is the single fill notification. The
                // measured request→fill latency rides that card instead, and
                // only on the latency-debug hosts (isLatencyDebugHost) — it is
                // a dev instrument, not product copy.

                onSuccess?.();
            } catch (error) {
                logError('Open', error);
                const handled = onError?.(error) === true;
                const errorMessage = classifyError(error);
                patchSlot(request.coin, (prev) => ({
                    ...prev,
                    status: 'failed',
                    error: errorMessage,
                    executionPhase: null,
                    clientRequestId: null,
                }));
                if (!handled) {
                    toast.error(errorMessage, { duration: TOAST_DURATION });
                }
            } finally {
                clearPhaseFallback();
                activeOpenRef.current = null;
                executionLockRef.current = false;
            }
        },
        [clearPhaseFallback, patchSlot, slots],
    );

    /**
     * Places a GTC limit entry. Unlike a market open this normally does NOT
     * produce a position: the happy path parks the slot in `resting`, where the
     * only moves are cancel and fill-now. The backend still answers `Open` when
     * the price crossed the book on arrival, and that case is an ordinary fill.
     */
    const executeLimitOpen = useCallback(
        async ({
            backend,
            request,
            positionType,
            positionAmount,
            onSuccess,
            onError,
        }: ExecuteLimitOpenParams) => {
            if (executionLockRef.current) {
                logStep('Limit open skipped: another execution is in-flight');
                return;
            }
            // Same per-coin rule as the market open: the pair's active slot is
            // held by a resting order just as much as by a position.
            const openKey = key(request.coin);
            const existing = openKey === null ? undefined : slots[openKey];
            if (
                existing &&
                existing.status !== 'idle' &&
                existing.status !== 'failed'
            ) {
                logStep(
                    'Limit open skipped: this market already has an order',
                    {
                        coin: request.coin,
                    },
                );
                return;
            }

            executionLockRef.current = true;
            const clientRequestId = uuid();
            const correlatedRequest = { ...request, clientRequestId };
            try {
                logStep('Limit open: submitting', correlatedRequest);
                patchSlot(request.coin, () => ({
                    ...INITIAL_STATE,
                    status: 'submitting',
                    coin: request.coin,
                    positionType,
                    positionAmount,
                    // No entry price yet, and deliberately not the limit price:
                    // a resting order has no fill, so nothing may render it as
                    // one. `limitPrice` below is what the resting card reads.
                    entryPrice: null,
                    leverage: request.leverage,
                    limitPrice: String(request.limitPrice),
                    clientRequestId,
                }));

                const submitTime = performance.now();
                const result = await backend.openLimitOrder(correlatedRequest);
                const fillMs = Math.round(performance.now() - submitTime);
                logStep('Limit open: accepted', {
                    txHash: result.txHash,
                    status: result.status,
                });

                if (result.status === 'Open') {
                    // crossed the book on arrival: an ordinary fill, with the
                    // limit price kept so the row still reads as limit-origin.
                    const entry = Number(result.entryPrice);
                    patchSlot(request.coin, (prev) => ({
                        ...prev,
                        status: 'open',
                        tradeHash: result.txHash,
                        openTxHash: result.txHash,
                        entryPrice: Number.isFinite(entry)
                            ? entry
                            : Number(result.limitPrice),
                        leverage: result.leverage,
                        limitPrice: result.limitPrice,
                        executionPhase: null,
                        clientRequestId: null,
                        openedAt: Date.now(),
                        fillMs,
                        openSource: 'live',
                    }));
                } else {
                    patchSlot(request.coin, (prev) => ({
                        ...prev,
                        status: 'resting',
                        tradeHash: result.txHash,
                        openTxHash: result.txHash,
                        leverage: result.leverage,
                        limitPrice: result.limitPrice,
                        executionPhase: null,
                        clientRequestId: null,
                        openedAt: Date.now(),
                        fillMs: null,
                        openSource: 'live',
                    }));
                }

                onSuccess?.({ status: result.status });
            } catch (error) {
                logError('Limit open', error);
                const handled = onError?.(error) === true;
                const errorMessage = classifyError(error);
                patchSlot(request.coin, (prev) => ({
                    ...prev,
                    status: 'failed',
                    error: errorMessage,
                    executionPhase: null,
                    clientRequestId: null,
                }));
                if (!handled) {
                    toast.error(errorMessage, { duration: TOAST_DURATION });
                }
            } finally {
                executionLockRef.current = false;
            }
        },
        [patchSlot, slots],
    );

    /**
     * Cancel ("Cancel order") or take at market ("Fill now") the coin's resting
     * order. One implementation because the two differ only in which endpoint
     * they call and in what a success means — and because BOTH can come back
     * `Open`: a GTC can fill at any moment, including while the request is in
     * flight, and that is not an error but the position the user now holds.
     */
    const runLimitAction = useCallback(
        async (
            action: 'cancel' | 'fill',
            { backend, coin, onSuccess, onError }: ExecuteLimitActionParams,
        ) => {
            if (executionLockRef.current) {
                logStep('Limit action skipped: another execution is in-flight');
                return;
            }
            const actionKey = key(coin);
            const target = actionKey === null ? undefined : slots[actionKey];
            if (!target || target.status !== 'resting') return;

            // Everything the order knew about itself, captured BEFORE the
            // request. "Fill now" returns a brand-new trade whose response
            // carries only a hash, a size and a leverage — the side, the margin
            // and the time the user committed exist nowhere else, and a position
            // view without them renders blank.
            const before = {
                tradeHash: target.tradeHash,
                positionType: target.positionType,
                positionAmount: target.positionAmount,
                leverage: target.leverage,
                limitPrice: target.limitPrice,
            };

            executionLockRef.current = true;
            // `closing` is what every button already spins on, and it is the
            // honest word for both: the resting order is going away either way.
            patchSlot(coin, (prev) => ({
                ...prev,
                status: 'closing',
                pendingAction: action,
            }));
            try {
                logStep(`Limit ${action}: submitting`, { coin });
                const result =
                    action === 'cancel'
                        ? await backend.cancelLimitOrder({ coin })
                        : await backend.executeLimitOrder({ coin });
                logStep(`Limit ${action}: accepted`, result);

                if (result.status === 'Cancelled') {
                    patchSlot(coin, (prev) => ({
                        ...INITIAL_STATE,
                        coin: prev.coin,
                    }));
                    onSuccess?.({
                        status: 'Cancelled',
                        tradeHash: result.tradeHash,
                    });
                    return;
                }

                // An order that had already filled keeps the hash it filled
                // under; a real "fill now" opened a NEW trade under a new one.
                const filledBefore =
                    'filledBeforeExecute' in result &&
                    result.filledBeforeExecute;
                const responseHash =
                    'txHash' in result ? result.txHash : result.tradeHash;
                const tradeHash = filledBefore
                    ? (before.tradeHash ?? responseHash)
                    : responseHash;
                const limitEntry =
                    before.limitPrice !== null
                        ? Number(before.limitPrice)
                        : null;
                patchSlot(coin, (prev) => ({
                    ...prev,
                    status: 'open',
                    tradeHash,
                    openTxHash: tradeHash,
                    // Rebuilt from the order, not merged into whatever the slot
                    // holds now: a `Cancelled` push for the old hash may have
                    // landed mid-request and emptied it.
                    positionType: prev.positionType ?? before.positionType,
                    positionAmount:
                        prev.positionAmount ?? before.positionAmount,
                    // The entry is provisional: neither endpoint reports a fill
                    // price. The limit price is the closest honest stand-in and
                    // the exchange's own entry replaces it on the next position
                    // read (livePosition wins over this everywhere it is shown).
                    entryPrice:
                        prev.entryPrice ??
                        (Number.isFinite(limitEntry) ? limitEntry : null),
                    leverage:
                        ('leverage' in result ? result.leverage : null) ??
                        prev.leverage ??
                        before.leverage,
                    limitPrice: prev.limitPrice ?? before.limitPrice,
                    openedAt: prev.openedAt ?? Date.now(),
                    openSource: 'live',
                    pendingAction: null,
                }));
                onSuccess?.({ status: 'Open', tradeHash });
            } catch (error) {
                logError(`Limit ${action}`, error);
                const handled = onError?.(error) === true;
                const errorMessage = classifyError(error);
                // The order is genuinely gone (filled elsewhere, already
                // cancelled, swept): drop the slot instead of parking a resting
                // card the user cannot act on. Anything else left the order ON
                // the book, so put the card back rather than hiding it behind
                // `failed` — the user still has an order to manage.
                const gone =
                    isApiError(error) &&
                    error.body?.code === NO_RESTING_ORDER_CODE;
                patchSlot(coin, (prev) =>
                    gone
                        ? { ...INITIAL_STATE, coin: prev.coin }
                        : {
                              ...prev,
                              status: 'resting',
                              error: errorMessage,
                              pendingAction: null,
                          },
                );
                if (!handled) {
                    toast.error(errorMessage, { duration: TOAST_DURATION });
                }
            } finally {
                // the request is over either way; a later `Cancelled` push is
                // then somebody else's cancel, not a step of this one.
                patchSlot(coin, (prev) =>
                    prev.pendingAction === null
                        ? prev
                        : { ...prev, pendingAction: null },
                );
                executionLockRef.current = false;
            }
        },
        [patchSlot, slots],
    );

    const executeLimitCancel = useCallback(
        (params: ExecuteLimitActionParams) => runLimitAction('cancel', params),
        [runLimitAction],
    );

    const executeLimitFill = useCallback(
        (params: ExecuteLimitActionParams) => runLimitAction('fill', params),
        [runLimitAction],
    );

    const applyExecutionPhase = useCallback(
        (update: TradeNotificationExecutionPhase) => {
            const active = activeOpenRef.current;
            if (
                !active ||
                active.clientRequestId !== update.clientRequestId ||
                active.coin.toLowerCase() !== update.coin.toLowerCase()
            ) {
                return;
            }

            clearPhaseFallback();
            patchSlot(active.coin, (prev) =>
                prev.status === 'submitting' &&
                prev.clientRequestId === update.clientRequestId
                    ? { ...prev, executionPhase: update.phase }
                    : prev,
            );
        },
        [clearPhaseFallback, patchSlot],
    );

    const executeClose = useCallback(
        async ({ backend, coin, onSuccess, onError }: ExecuteCloseParams) => {
            if (executionLockRef.current) {
                logStep('Close skipped: another execution is in-flight');
                return;
            }
            // Gate on THIS market's slot. Reading the one global status was what
            // would have let a CLOSE on the charted market run against whatever
            // coin the hook last touched.
            const closeKey = key(coin);
            const target = closeKey === null ? undefined : slots[closeKey];
            if (
                !target ||
                (target.status !== 'open' && target.status !== 'pending')
            ) {
                return;
            }

            executionLockRef.current = true;
            clearPhaseFallback();
            activeOpenRef.current = null;
            try {
                logStep('Close: submitting', { coin });
                patchSlot(coin, (prev) => ({ ...prev, status: 'closing' }));

                const closeSubmitTime = performance.now();
                const result = await backend.closeTrade({ coin });
                const closeMs = Math.round(performance.now() - closeSubmitTime);
                logStep('Close: accepted', { txHash: result.txHash, closeMs });
                // No separate "Closed" toast: the POSITION CLOSED card (SpeedResultFx)
                // is the single close notification. The measured latency rides that
                // card via onSuccess, and only on the latency-debug hosts.

                // the agent close is synchronous + authoritative: a non-error
                // response means the reduce-only IOC filled. optimistically return
                // to idle now instead of waiting on the WS Closed push (which agent
                // mode may never send); the WS event and REST reconciliation remain
                // idempotent no-ops if they arrive after.
                patchSlot(coin, (prev) => ({
                    ...INITIAL_STATE,
                    coin: prev.coin,
                }));

                onSuccess?.({ closeMs });
            } catch (error) {
                logError('Close', error);
                const handled = onError?.(error) === true;
                const errorMessage = classifyError(error);
                patchSlot(coin, (prev) => ({
                    ...prev,
                    status: 'failed',
                    error: errorMessage,
                }));
                if (!handled) {
                    toast.error(errorMessage, { duration: TOAST_DURATION });
                }
            } finally {
                executionLockRef.current = false;
            }
        },
        [clearPhaseFallback, patchSlot, slots],
    );

    /**
     * Apply a trade-status WS push (or reconciliation result) for `tradeHash`.
     * Only acts on the currently-tracked trade.
     */
    const applyTradeUpdate = useCallback(
        (
            tradeHash: string,
            wsStatus: TradeStatus,
            wsEntryPrice?: number,
            wsLimitPrice?: string,
        ) => {
            // Route by tradeHash to its OWN market. Comparing against a single
            // tracked hash meant a push for any other open position was dropped
            // on the floor — with one position that was the same thing.
            const coin = coinForTradeHash(tradeHash);
            if (coin === null) return;

            if (wsStatus === 'Resting') {
                patchSlot(coin, (prev) =>
                    prev.status === 'submitting' || prev.status === 'resting'
                        ? {
                              ...prev,
                              status: 'resting',
                              ...(wsLimitPrice !== undefined
                                  ? { limitPrice: wsLimitPrice }
                                  : {}),
                          }
                        : prev,
                );
                return;
            }

            // Terminal, and NOT a close: no position ever existed, so there is
            // nothing to play a close visual for. The slot simply goes away.
            //
            // Except during a "fill now", where the backend's OWN cancel of the
            // resting order publishes this for the old hash before it opens the
            // position. Resetting there would empty the slot the market open is
            // about to land in.
            if (wsStatus === 'Cancelled') {
                patchSlot(coin, (prev) =>
                    prev.status === 'resting' ||
                    (prev.status === 'closing' &&
                        prev.pendingAction === 'cancel')
                        ? { ...INITIAL_STATE, coin: prev.coin }
                        : prev,
                );
                return;
            }

            if (wsStatus === 'Open') {
                patchSlot(coin, (prev) =>
                    prev.status === 'pending' ||
                    prev.status === 'open' ||
                    // the limit order filled: from here it is an ordinary
                    // running position and the close view takes over.
                    prev.status === 'resting'
                        ? {
                              ...prev,
                              status: 'open',
                              entryPrice:
                                  wsEntryPrice ??
                                  prev.entryPrice ??
                                  (prev.limitPrice !== null
                                      ? Number(prev.limitPrice)
                                      : null),
                              openedAt: prev.openedAt ?? Date.now(),
                          }
                        : prev,
                );
                return;
            }

            if (wsStatus === 'Failed') {
                const inFlight: TradeExecutionStatus[] = [
                    'submitting',
                    'pending',
                    'open',
                    'closing',
                    // an ambiguous placement the reconciler proved never landed
                    'resting',
                ];
                patchSlot(coin, (prev) =>
                    inFlight.includes(prev.status)
                        ? {
                              ...prev,
                              status: 'failed',
                              error: 'Trade failed. Please try again.',
                          }
                        : prev,
                );
                return;
            }

            const closeOutcomes: TradeStatus[] = [
                'Closed',
                'TakeProfit',
                'StopLoss',
                'Liquidated',
            ];
            if (closeOutcomes.includes(wsStatus)) {
                patchSlot(coin, (prev) => ({
                    ...INITIAL_STATE,
                    coin: prev.coin,
                }));
            }
        },
        [coinForTradeHash, patchSlot],
    );

    return {
        /** every market this client is driving, keyed by upper-cased coin. */
        slots,
        executeOpen,
        executeClose,
        executeLimitOpen,
        executeLimitCancel,
        executeLimitFill,
        applyTradeUpdate,
        applyExecutionPhase,
        clearExecutionPhase,
        reset,
        hydrateFromRemoteTrade,
        clearHydratedTrade,
    };
}
